import React, { useState, useMemo } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import Loader from "../../../shared/ui/Loader.js";
import { useGlobalContext } from "../../../Config";
import { getVersion } from "../../../services/ContratoService.js";
import {
  guardarConfiguracionPredeterminada,
  actualizarConfiguracionDetalles,
} from "../../../services/ClienteService.js";
import {
  cantidadPorTipo,
  distribuirPorCantidad,
  normalizarDocumentos,
  validarAsignacionDocumentos,
} from "../../../utilities/documentosCobro.js";
import TablaCobroCategoria from "../../../shared/components/documentos/TablaCobroCategoria.js";
import BoxEscenarioDocumentos from "../../../shared/components/documentos/BoxEscenarioDocumentos.js";

const CotizacionActions = ({
  cotizacion,
  loading,
  loadingAsig,
  handleAgregarProducto,
  openDialog,
  handleShowResumenMensual,
  guardarCotizacion,
  checkDatosFaltantesCliente,
  handleTerminar,
  handleRechazar,
  cerrarContrato,
  diasAviso,
  setDiasAviso,
  cantidadDiasAvisoContrato,
  diasFirma,
  setDiasFirma,
  cantidadDiasFirmaContrato,
  showCerrarContrato,
  setShowCerrarContrato,
  idCliente,
  idEmpresa,
  configuracionPredeterminada,
  asignacionesVigentesFuturas,
  catalogo,
  handleShowModificarAsignacion,
  handleShowRenovarAsignacion,
  detallesCobro,
  setDetallesCobro,
  handleTablaChange,
  flattenedDataArriendo,
  servicios,
}) => {
  const { urlContratos, baseUrl } = useGlobalContext();

  // Categorías de documento desde el catálogo (tipo_documento); un tipo
  // nuevo dado de alta en BD escala sin cambios de código
  const categorias = (Array.isArray(catalogo) ? catalogo : [])
    .map((tipo) => tipo.descripcion)
    .filter(Boolean);

  // Ítems existentes (asignaciones vigentes/futuras del cliente) normalizados
  const itemsExistentesBase = useMemo(
    () =>
      (Array.isArray(asignacionesVigentesFuturas)
        ? asignacionesVigentesFuturas
        : []
      ).flatMap((asig) =>
        (Array.isArray(asig.detalles) ? asig.detalles : []).map((d) => ({
          id: d.id,
          descripcion: d.descripcion,
          relacion: d.relacion,
          cobro: d.cobro,
          agrupamiento: Number(d.agrupamiento) || null,
          asignacionId: asig.id,
          vigencia: asig.vigencia,
          existente: true,
        }))
      ),
    [asignacionesVigentesFuturas]
  );

  const [showAgrupacionDocs, setShowAgrupacionDocs] = useState(false);
  // Escenario editable por tipo de documento (box superior)
  const [configEditable, setConfigEditable] = useState([]);
  // Ítems existentes editables en el diálogo (se persisten en lote al Asignar)
  const [itemsExistentes, setItemsExistentes] = useState([]);

  const combinados = useMemo(
    () => [...detallesCobro, ...itemsExistentes],
    [detallesCobro, itemsExistentes]
  );

  // Cantidad de documentos vigente por tipo
  const cantidadesPorCobro = useMemo(
    () => cantidadPorTipo(configEditable),
    [configEditable]
  );

    const totalesPorCobro = useMemo(() => {
    const acc = {};
    categorias.forEach((cat) => {
      acc[cat] = { total: 0, nuevos: 0, existentes: 0 };
    });
    combinados.forEach((i) => {
      const t = acc[i.cobro];
      if (!t) return;
      t.total += 1;
      if (i.existente) {
        t.existentes += 1;
      } else {
        t.nuevos += 1;
      }
    });
    return acc;
  }, [combinados, categorias]);

  // Dropdowns "Documento N" por tipo: exactamente las opciones definidas en
  // el box (valores 1..cantidad, numeración por tipo)
  const optionsByCobro = useMemo(() => {
    const map = {};
    Object.entries(cantidadesPorCobro).forEach(([cat, cantidad]) => {
      map[cat] = Array.from({ length: Math.max(cantidad ?? 0, 0) }, (_, idx) => ({
        label: `${cat} ${idx + 1}`,
        value: idx + 1,
      }));
    });
    return map;
  }, [cantidadesPorCobro]);

  // Al cambiar la cantidad de un tipo, sus ítems (nuevos + existentes) se
  // re-distribuyen round-robin en los nuevos documentos
  const handleCambiarConfig = (codigo, descripcion, patch) => {
    const nuevaConfig = (configEditable || []).map((c) =>
      c.codigo === codigo ? { ...c, ...patch } : c
    );
    setConfigEditable(nuevaConfig);
    const cantidades = cantidadPorTipo(nuevaConfig);
    setItemsExistentes((prev) =>
      distribuirPorCantidad(prev, cantidades, descripcion)
    );
    setDetallesCobro((prev) =>
      distribuirPorCantidad(prev, cantidades, descripcion)
    );
  };

  const handleAsignar = async () => {
    // La configuración de documentos es por (cliente, empresa); sin empresa
    // no es posible persistirla
    if (!Number(idEmpresa)) {
      window.alert(
        "No se pudo determinar la empresa de la cotización. No es posible guardar la configuración de documentos."
      );
      return;
    }

    // Los ítems nuevos conservan la asignación de documento definida en el
    // diálogo (agrupamiento = número de documento dentro de su tipo, ya
    // validado)
    const finalItems = detallesCobro.map((item) => ({ ...item }));

    finalItems.forEach((item) => {
      const source = flattenedDataArriendo.some((x) => x.id === item.id)
        ? "flattened"
        : servicios.some((x) => x.id === item.id)
        ? "servicios"
        : "unicos";

      // Persistir la asignación de documento; el estado consolidado lo
      // infiere el backend de la distribución
      handleTablaChange(item, item.agrupamiento, source, "agrupamiento");
    });

    // 1) Persistir la configuración predeterminada del cliente para la
    //    empresa de la cotización (tipo "consolidadas" guarda explícitamente
    //    la cantidad de documentos)
    try {
      await guardarConfiguracionPredeterminada(baseUrl, {
        idCliente,
        idEmpresa: Number(idEmpresa),
        configuracion: (configEditable || []).map((c) => ({
          codigo: c.codigo,
          cantidadDocumentos: Number(c.cantidadDocumentos) || 1,
        })),
      });
    } catch (error) {
      window.alert(
        error?.message ||
          "No se pudo guardar la configuración del cliente. No se realizó la asignación."
      );
      return;
    }

    // 2) Persistir en lote los ítems existentes (asignaciones vigentes/futuras)
    //    que fueron reorganizados en el diálogo
    const existentesEditados = itemsExistentes.filter((i) => {
      const original = itemsExistentesBase.find((b) => b.id === i.id);
      if (!original) return false;
      return (
        (Number(original.agrupamiento) || 0) !==
        (Number(i.agrupamiento) || 0)
      );
    });
    if (existentesEditados.length > 0) {
      try {
        await actualizarConfiguracionDetalles(baseUrl, {
          detalles: existentesEditados.map((i) => ({
            id: i.id,
            agrupamiento: Number(i.agrupamiento),
          })),
        });
      } catch (error) {
        window.alert(
          error?.message ||
            "No se pudo actualizar la configuración de las asignaciones vigentes/futuras. No se realizó la asignación."
        );
        return;
      }
    }

    // ------------- se llama a cerrarContrato COTIZACION -> ASIGNACION---------------
    cerrarContrato(finalItems);
  };

  async function handleValidar() {
    try {
      await getVersion(urlContratos);
    } catch (error) {
      window.alert(
        "El servicio de contratos no está disponible en este momento. No es posible asignar ahora."
      );
      return;
    }

    // Cada ítem gestionado debe estar asignado a uno de los documentos
    // definidos para su tipo (cantidad dinámica del box)
    const error = validarAsignacionDocumentos(combinados, cantidadesPorCobro);
    if (error) {
      window.alert(
        `⚠️ ${error} Puedes cambiar la cantidad de documentos en el box o corregir la asignación de los ítems.`
      );
      return;
    }

    if (
      window.confirm(
        "✅ Configuración válida: todos los ítems están asignados a un documento. ¿Deseas guardar los cambios y asignar?"
      )
    ) {
      handleAsignar();
    }
  }

  function handleLimpiarAgrupaciones() {
    if (window.confirm("¿Está seguro que desea reiniciar la configuración?")) {
      const cantidades = cantidadPorTipo(configEditable);
      setDetallesCobro(distribuirPorCantidad(detallesCobro, cantidades));
      setItemsExistentes(distribuirPorCantidad(itemsExistentes, cantidades));
    }
  }

  function onAsignarDocumento(rowData, e) {
    // Ítems existentes: solo estado local (se persisten en lote al Asignar)
    if (rowData.existente) {
      setItemsExistentes((prev) =>
        prev.map((i) =>
          i.id === rowData.id ? { ...i, agrupamiento: e.value ?? null } : i
        )
      );
      return;
    }
    // Actualizar estado local para feedback inmediato en UI
    setDetallesCobro((prev) =>
      prev.map((i) =>
        i.id === rowData.id ? { ...i, agrupamiento: e.value ?? null } : i
      )
    );
    // Persistir en la fuente correspondiente
    handleTablaChange(
      rowData,
      e.value ?? 0,
      flattenedDataArriendo.some((item) => item.id === rowData.id)
        ? "flattened"
        : servicios.some((item) => item.id === rowData.id)
        ? "servicios"
        : "unicos",
      "agrupamiento"
    );
  }

  const parseFechaDMY = (str) => {
    if (!str) return null;
    const [dia, mes, anio] = String(str)
      .trim()
      .split("/")
      .map(Number);
    if (!dia || !mes || !anio) return null;
    const d = new Date(anio, mes - 1, dia);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const fechaActual = new Date();
  fechaActual.setHours(0, 0, 0, 0);

  const mostrarModificarAsignacion = (() => {
    const hasta = parseFechaDMY(cotizacion?.hasta);

    if (hasta) {
      return fechaActual <= hasta;
    }
    return false;
  })();

  const mostrarRenovarAsignacion = (() => {
    const desde = parseFechaDMY(cotizacion?.desde);

    // Nueva regla: si 'desde' es mayor a hoy, no mostramos
    if (desde && fechaActual < desde) return false;

    // Solo 'desde': mostrar si hoy es mayor o igual a 'desde'
    if (desde) return fechaActual >= desde;

    return false;
  })();

  const estadoActual = cotizacion?.estado?.estado;
  const estadoValido = Boolean(estadoActual);
  const bloquearCotizacion = new Set([
    "ASIGNACIÓN",
    "ANULADA",
    "REVISIÓN",
    "RECHAZADA",
    "RENOVACIÓN",
  ]);
  const bloquearAprobacion = new Set([
    "ASIGNACIÓN",
    "ANULADA",
    "RECHAZADA",
    "COTIZACIÓN",
  ]);

  function handleDownloadContrato() {
    window.alert("Descargando contrato...");
  }

  return (
    <div className="p-fluid formgrid grid">
      {
        estadoValido && !bloquearCotizacion.has(estadoActual) && 
      (
        <>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Agregar Producto"
              icon="pi pi-box"
              className="mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%" }}
              onClick={handleAgregarProducto}
              disabled={loading}
              severity="primary"
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Agregar Subproducto"
              icon="pi pi-box"
              className="mt-4 mb-4 p-button-rounded p-button"
              onClick={() => openDialog("subproducto")}
              style={{ width: "100%"}}
              disabled={loading}
              severity="primary"
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Agregar Servicio"
              icon="pi pi-wrench"
              className="mt-4 mb-4 p-button-rounded p-button"
              onClick={() => openDialog("servicio")}
              style={{ width: "100%" }}
              disabled={loading}
              severity="primary"
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Ver Resumen Mensual"
              icon="pi pi-calendar"
              onClick={handleShowResumenMensual}
              className="mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%" }}
              severity="primary"
              disabled={loading}
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Guardar Cotización"
              icon="pi pi-save"
              className="mt-4 mb-4 p-button-rounded p-button"
              onClick={guardarCotizacion}
              style={{ width: "100%" }}
              severity="primary"
              disabled={loading}
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Terminar"
              icon="pi pi-check"
              className="p-button-success mt-4 mb-4 p-button-rounded p-button"
              onClick={handleTerminar}
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
        </>
      )}
      {
        estadoValido && !bloquearAprobacion.has(estadoActual) && 
      (
        <>
          {/* Primera fila - botones expandidos */}
          <div className="col-12 md:col-2" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Agregar Producto"
              icon="pi pi-box"
              className="mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%" }}
              onClick={handleAgregarProducto}
              disabled={loading}
              raised
              severity="primary"
            />
          </div>
          <div className="col-12 md:col-2" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Agregar Subproducto"
              icon="pi pi-box"
              className="mt-4 mb-4 p-button-rounded p-button"
              onClick={() => openDialog("subproducto")}
              style={{ width: "100%", color: "white" }}
              disabled={loading}
              raised
              severity="primary"
            />
          </div>
          <div className="col-12 md:col-2" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Agregar Servicio"
              icon="pi pi-wrench"
              className=" mt-4 mb-4 p-button-rounded p-button"
              onClick={() => openDialog("servicio")}
              style={{ width: "100%" }}
              disabled={loading}
              raised
              severity="primary"
            />
          </div>
          <div className="col-12 md:col-3" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Ver Resumen Mensual"
              icon="pi pi-calendar"
              onClick={handleShowResumenMensual}
              className="mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%" }}
              disabled={loading}
              raised
              severity="primary"
            />
          </div>
          <div className="col-12 md:col-3" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Guardar Cotización"
              icon="pi pi-save"
              className="p-button-primary mt-4 mb-4 p-button-rounded p-button"
              onClick={guardarCotizacion}
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
          
          {/* Segunda fila - botones centrados */}
          <div className="col-12" style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
            <Button
              label="Rechazar"
              icon="pi pi-times"
              className="mt-4 mb-4 p-button-rounded p-button"
              onClick={handleRechazar}
              style={{ minWidth: "150px" }}
              disabled={loading}
              raised
              severity="danger"
            />
            <Button
              label="Aprobar"
              icon="pi pi-check"
              className="mt-4 mb-4 p-button-rounded p-button"
              onClick={checkDatosFaltantesCliente}
              style={{ minWidth: "150px" }}
              disabled={loading}
              raised
              severity="success"
            />
          </div>

          {/* Dialog para cerrar contrato */}
          <Dialog
            visible={showCerrarContrato}
            onHide={() => setShowCerrarContrato(false)}
            modal
            className="dialog-responsive"
            style={{ maxWidth: window.innerWidth <= 600 ? "95vw" : "60vw", width: window.innerWidth <= 600 ? "95%" : "90%", margin: window.innerWidth <= 600 ? "2.5vw" : "auto"}}
            closable={false}
            onShow={() => {
              // Cantidad por tipo: la guardada en BD (clampeada al total de
              // ítems); si no existe se deriva del total (1 documento por
              // ítem). Sin conceptos separado/consolidado
              const baseItems = itemsExistentesBase.map((i) => ({ ...i }));
              const conteoGlobal = [...detallesCobro, ...baseItems].reduce(
                (acc, i) => {
                  acc[i.cobro] = (acc[i.cobro] || 0) + 1;
                  return acc;
                },
                {}
              );
              const configInicial = (Array.isArray(catalogo) ? catalogo : []).map(
                (t) => {
                  const descripcion = t.descripcion;
                  const codigo = t.codigo ?? descripcion;
                  const total = Math.max(conteoGlobal[descripcion] ?? 0, 1);
                  const guardada = (Array.isArray(configuracionPredeterminada)
                    ? configuracionPredeterminada
                    : []
                  ).find(
                    (c) =>
                      c.codigo === codigo ||
                      (t.codigo == null &&
                        (c.descripcion ?? c.documentoCobro) === descripcion)
                  );
                  const guardadaCantidad = Number(guardada?.cantidadDocumentos);
                  const cantidad =
                    Number.isFinite(guardadaCantidad) && guardadaCantidad >= 1
                      ? Math.min(guardadaCantidad, total)
                      : total;
                  return { codigo, descripcion, cantidadDocumentos: cantidad };
                }
              );
              setConfigEditable(configInicial);

              const cantidades = cantidadPorTipo(configInicial);
              // Ítems existentes: conservan su distribución guardada si es
              // válida; los tipos con valores legacy/obsoletos se re-distribuyen
              setItemsExistentes(normalizarDocumentos(baseItems, cantidades));

              // Ítems nuevos sin asignación previa: round-robin según la
              // cantidad definida
              if (!detallesCobro.some((item) => Number(item.agrupamiento) > 0)) {
                setDetallesCobro(
                  distribuirPorCantidad(detallesCobro, cantidades)
                );
              }
            }}
          >
            <>
              {loadingAsig ? (
                <>
                  <Loader />
                </>
              ) : (
                <>
                  <div className="p-fluid formgrid grid responsive-dialog-content">
                    <div className="col-12 md:col-12" >
                    
                      <div div style={{ textAlign: "center", position: "relative" }}>
                        <h2>Cerrando Cotización</h2>
                      </div>

                      <h6 className="mt-3">• ¿Con cuántos días de anticipación se debe notificar el vencimiento del contrato?</h6>
                      <span className="p-float-label mt-2 mb-3">
                        <Dropdown
                          id="daysDropdown"
                          value={diasAviso}
                          options={cantidadDiasAvisoContrato}
                          onChange={(e) => setDiasAviso(e.value)}
                          optionLabel="label"
                          placeholder="Seleccionar"
                          disabled={loadingAsig}
                          style={{ width: "100%", marginTop: "6px" }}
                        />
                        <label htmlFor="daysSelect"> Plazo para aviso</label>
                      </span>

                      <h6 className="mt-3">• Defina la cantidad de días que tiene el cliente para firmar este contrato: </h6>
                      <span className="p-float-label mt-2 mb-3">
                        <Dropdown
                          id="daysDropdown"
                          value={diasFirma}
                          options={cantidadDiasFirmaContrato}
                          onChange={(e) => setDiasFirma(e.value)}
                          optionLabel="label"
                          placeholder="Seleccionar días"
                          style={{ width: "100%", marginTop: "6px" }}
                        />
                        <label htmlFor="daysDropdown"> Plazo para firma </label>
                      </span>

                      {/* Box de escenario por tipo de documento */}
                      <BoxEscenarioDocumentos
                        catalogo={catalogo}
                        config={configEditable}
                        totalesPorCobro={totalesPorCobro}
                        onChangeConfig={handleCambiarConfig}
                        disabled={loadingAsig}
                      />

                      {/* agrupacion de documentos */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px" }}>
                        <h6 className="mt-3" style={{ margin: 0 }}>• Asignación de documentos (cada ítem debe estar asignado)</h6>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <Button
                            type="button"
                            icon={showAgrupacionDocs ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                            className="p-button-text p-button-sm"
                            onClick={() => setShowAgrupacionDocs((prev) => !prev)}
                            label={showAgrupacionDocs ? "Colapsar" : "Expandir"}
                          />
                          <Button
                            icon="pi pi-refresh"
                            severity="primary"
                            outlined
                            className="p-button-sm"
                            onClick={handleLimpiarAgrupaciones}
                            tooltip="Reiniciar configuración"
                            tooltipOptions={{ position: 'left' }}
                            disabled={loadingAsig}
                          />
                        </div>
                      </div>
                      {showAgrupacionDocs && (
                        <fieldset style={{border: "1px solid #ccc",padding: "10px",borderRadius: "5px" }}>
                          <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px", padding: "12px", marginBottom: "16px", fontSize: "0.95rem" }}>
                            <div style={{ fontWeight: 600, marginBottom: "6px" }}>¿Cómo funciona?</div>
                            <div>En el box de arriba defines cuántos documentos emitirá cada tipo (entre 1 y su total de ítems); al cambiarlo, los ítems se distribuyen automáticamente.</div>
                            <div>Asigna cada ítem (nuevos y vigentes) a uno de los documentos de su tipo; un documento con varios ítems se emite consolidado y con uno solo, por separado.</div>
                          </div>

                          {categorias.filter((cat) =>
                            combinados.some((i) => i.cobro === cat)
                          ).length === 0 ? (
                            <span className="text-500 text-sm">
                              No hay ítems para distribuir.
                            </span>
                          ) : (
                            categorias
                              .filter((cat) =>
                                combinados.some((i) => i.cobro === cat)
                              )
                              .map((cat) => (
                                <TablaCobroCategoria
                                  key={cat}
                                  categoria={cat}
                                  visible={true}
                                  items={combinados
                                    .filter((i) => i.cobro === cat)
                                    .sort(
                                      (a, b) =>
                                        (Number(a.agrupamiento) || 9999) -
                                          (Number(b.agrupamiento) || 9999) ||
                                        String(a.descripcion ?? "").localeCompare(
                                          String(b.descripcion ?? "")
                                        )
                                    )}
                                  loadingAsig={loadingAsig}
                                  options={optionsByCobro[cat] ?? []}
                                  onAsignarDocumento={onAsignarDocumento}
                                />
                              ))
                          )}

                        </fieldset>
                      )}
                    </div>
                    <div className="col-12 responsive-dialog-buttons" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '1rem' }}>
                      <Button
                        label="Volver"
                        icon="pi pi-arrow-left"
                        className="mt-4 mb-4 p-button-rounded p-button-md p-button"
                        onClick={() => {
                          if (window.confirm("¿Está seguro que desea volver? Se perderá la configuración ingresada.")) {
                            setShowCerrarContrato(false);
                          }
                        }}
                        outlined
                        disabled={loadingAsig}
                        raised
                      />

                      <Button
                        label="Asignar"
                        icon={loadingAsig ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        className="p-button-success mt-4 mb-4 p-button-rounded p-button-md p-button"
                        onClick={() => {
                          handleValidar();
                        }}
                        disabled={loadingAsig}
                        raised
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          </Dialog>
        </>
      )}

      {estadoValido && estadoActual === "ASIGNACIÓN" && (
        <>
          <div className="col-12 md:col-3"></div>
         
          <div className="col-12 md:col-2">
            <Button
              label="Modificar Asignación"
              icon="pi pi-cog"
              className="mt-4 mb-4 p-button-rounded p-button"
              onClick={handleShowModificarAsignacion}
              style={{ width: "100%", height: "50px" }}
              disabled={loading}
              severity="primary"
              raised
            />
          </div>
        

          <div className="col-12 md:col-2">
            <Button
              label="Ver Resumen Mensual"
              icon="pi pi-calendar"
              onClick={handleShowResumenMensual}
              className="mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%", height: "50px" }}
              disabled={loading}
              severity="primary"
              raised
            />
          </div>

          {mostrarModificarAsignacion && mostrarRenovarAsignacion ? (
            <div className="col-12 md:col-2">
              <Button
                label="Renovar Asignación"
                icon="pi pi-forward"
                className="p-button-info mt-4 mb-4 p-button-rounded p-button-md p-button"
                onClick={handleShowRenovarAsignacion}
                style={{ width: "100%", height: "50px" }}
                disabled={loading}
                raised
              />
            </div>
          ): 
            <div className="col-12 md:col-2"></div>
          }

          <div className="col-12 md:col-2">
            <Button
              label="Descargar Contrato"
              icon="pi pi-download"
              onClick={handleDownloadContrato}
              className="p-button-help mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%", height: "50px" }}
              disabled
              raised
            />
          </div>

        </>
      )}

      {estadoActual === "ANULADA" && (
        <>
          <div className="col-12 md:col-2">
            <Button
              label="Ver Resumen Mensual"
              icon="pi pi-calendar"
              onClick={handleShowResumenMensual}
              className="p-button-help mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%", height: "50px" }}
              disabled={loading}
              raised
            />
          </div>
        </>
      )}

    </div>
  );
};

export default CotizacionActions;
